import { trpc } from "@/lib/trpc";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";

/**
 * Hook para verificar permissões de páginas do usuário atual.
 *
 * - admin: acesso irrestrito a tudo (retorna true sempre)
 * - user: sem acesso (bloqueado no ProtectedRoute antes de chegar aqui)
 * - cliente: verifica a lista de páginas permitidas no banco
 * - lider/operador: acesso controlado pelo item.roles no Sidebar (retorna true aqui)
 */
export function usePagePermissions() {
  const { user, loading: authLoading } = useGoogleAuth();

  const { data: allowedPages, isLoading } = trpc.users.myPagePermissions.useQuery(undefined, {
    enabled: !!user && user.role === "cliente",
    staleTime: 30 * 1000, // 30 segundos — efeito rápido após salvar permissões
  });

  const loading = authLoading || (user?.role === "cliente" && isLoading);

  const hasAccess = (path: string): boolean => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.role === "user") return false;

    // lider/operador: acesso controlado pelo Sidebar via item.roles, não por hasAccess
    if (user.role === "lider" || user.role === "operador") return true;

    // cliente: verifica lista do banco
    if (isLoading) return false; // não exibe módulos antes de carregar permissões
    if (!allowedPages) return false;
    return allowedPages.includes(path);
  };

  return {
    hasAccess,
    allowedPages: user?.role === "admin" ? null : (allowedPages ?? []),
    loading,
  };
}
